import OrderDirectionsEnum from '../Enums/OrderDirectionsEnum';
import objectHelper from '../Helpers/objectHelper';
import type SearchParamsBase from './SearchParamsBase';

/**
 * Strongly-typed wrapper for the paginated-list request object services send to
 * the backend (see FRONTEND.md §9). Replaces the inline literal + JSON.stringify
 * pattern with `new SearchParams(searchItem, XxxSearchParams, ...).serializeForBackendCall()`.
 *
 * `searchItemType` must be passed because generics are erased at runtime: search
 * items usually reach a service as plain `{ ...searchItem }` spreads (the DataTable
 * spreads on every fetch), which drop the prototype and its cleaning methods.
 * `objectHelper.asInstanceOf` rehydrates the plain object onto the class prototype.
 */
export default class SearchParams<T extends SearchParamsBase> {
  SearchItem: T;
  OrderBy: string;
  OrderDirection: string;
  PageNumber: number;
  NumberOfElementsOnAPage: number;
  Command: string;

  constructor(
    searchItem: T | object,
    searchItemType: new (...args: any[]) => T,
    orderBy: string = '',
    orderDirection: string = OrderDirectionsEnum.Asc,
    pageNumber: number = 1,
    numberOfElementsOnAPage: number = 0,
    command: string = ''
  ) {
    this.SearchItem = objectHelper.asInstanceOf(searchItem, searchItemType);
    this.OrderBy = orderBy;
    this.OrderDirection = orderDirection;
    this.PageNumber = pageNumber;
    this.NumberOfElementsOnAPage = numberOfElementsOnAPage;
    this.Command = command;
  }

  /** Cleans the search item and returns the whole request as a JSON string for the `searchParams=` query param. Does not mutate the search item. */
  serializeForBackendCall(): string {
    return JSON.stringify({
      Command: this.Command,
      OrderBy: this.OrderBy,
      OrderDirection: this.OrderDirection,
      PageNumber: this.PageNumber,
      NumberOfElementsOnAPage: this.NumberOfElementsOnAPage,
      SearchItem: this.SearchItem.cleanForBackendCall(),
    });
  }
}
